<template>
    <div>
        <h3>本文を管理する</h3>
        <div>
            <p>
                小説の本文を追加する方法は２種類あります。
                ひとつは直接本文を記述する方法、もうひとつは、小説全体のテキストファイルをアップロードする方法です。
            </p>
            <h4>本文のアップロード</h4>
            <p class="text-danger">
                長編小説のみとなります。
            </p>
            <img src="/img/help/novel/02.png" alt="本文アップロード" class="img-thumbnail">
            <p>
                小説の「本文管理」から、本文管理画面を開いてください。
                <strong>本文がまだ一つも登録されていない場合</strong>、「ファイル」ボタンが表示されます。
                小説全体のテキストファイルを、「ファイル」ボタンを押して表示されるファイルアップロード画面から選択し、
                本文タイトルのパターンを記入してアップロードを行います。
            </p>
            <h5>文字コード</h5>
            <p>
                本文の文字コードはUTF-8かShift_JISを自動判定します。
                ただ、この自動判定は絶対ではなく、機種依存文字やその他の想定外の文字が含まれることで、
                判定に失敗することがあります。
            </p>
            <h5>本文タイトル</h5>
            <p>
                本文のタイトルが「第１話〜」「第２話〜」のように一定のパターンがある場合、「第」を設定することで、
                その行を本文のタイトル行とみなします。
            </p>
            <p>
                このパターンが無い場合、アップロードするテキストファイルの方で、本文タイトルの先頭に同一の文字
                ――例えば$などを入力し、本文タイトルに「$」を設定します。更に「タイトル設定文字を削除する」
                にチェックを入れることで、パターン文字の「$」を除去した状態で本文タイトルを設定してくれます。
            </p>
            <p>
                例：<br>
                「$一、はじまりの合図」→「一、はじまりの合図」
            </p>
            <h5>解析結果</h5>
            <p>
                アップロードを行うと、本文のタイトルと見出しの一部を一覧表示します。
                本文の無いタイトルは「章」と見なされます。
                その分解に問題がなければ、確定を行ってください。本文が一括登録されます。<br>
                <strong>登録直後の本文は非公開状態になります。</strong>
            </p>
            <h5>小説家になろう解析</h5>
            <p>
                小説家になろう（あるいはムーンライト、ミッドナイト、ノクターンノベルズ）でバックアップによってダウンロードしたファイルを登録することができます。
                小説家になろう解析をONにして、ダウンロードしたファイルをそのままアップロードしてください。
            </p>
            <p>
                このファイルはBOMなしUTF-8でダウンロードしたファイルに限られます。
                Shift_JISなど他の文字コードには対応していません。
            </p>
            <h5>自動解析</h5>
            <p>
                小説本文が適切に字下げされていれば、本文タイトルを空欄のままアップロードすることで、自動解析します。
                不適切な字下げや特殊な囲い文字が含まれると、その部分が本文タイトルと見なされることがあります。
            </p>

            <h4>本文を記述する</h4>
            <p>
                本文追加ボタンから、本文編集画面を開きます。
                <router-link to="/help/novel/episode">→本文を記述する</router-link>
            </p>

            <h4>章を追加する</h4>
            <p>
                本文一覧で「章」を追加することができます。
                章はかならず末尾に追加されるので、適切な位置に順番を変更してください。
            </p>

            <h4>本文の順番を変更する</h4>
            <p>
                本文を追加すると、常に最後に追加されます。
                例えば後からプロローグを追加する際には、一旦本文を追加し、本文を記述して保存します。
                本文一覧で、追加した本文のハンドル（
                <i class="fas fa-grip-vertical text-muted handle"></i>
                ）をドラッグして、移動したいところにドロップします。
                最後に「順番 更新」ボタンをクリックして確定します。
                このボタンを押下するまでは順番の変更は確定されません。
            </p>
            <h5>公開日の自動変更</h5>
            <p>
                既に公開されている作品の先頭に未公開のプロローグを移動した場合、
                次のエピソードの公開日と同一日時が公開日として自動的に設定されます。
                この場合は「順番 予定 更新」という確定ボタンが表示されるので、
                これをクリックすることで、順番と公開日が確定します。
            </p>

            <h4>タイトルを一括で変更する</h4>
            <p>
                本文一覧画面には「タイトル」ボタンが表示されます。
                これをクリックすると、タイトルの一括変更ウインドウが開きます。
                ここでタイトルを変更し、保存することで、本文を一つ一つ開いてタイトルを逐次変更する手間が省けます。
            </p>
            <h5>ナンバリングの自動正規化</h5>
            <p>
                タイトルにアラビア数字でナンバリングが指定されているとき、タイトル一括変更ウインドウを開くと、
                自動的にナンバリングの正規化を行います。
            </p>
            <img src="/img/help/novel/03.png" alt="タイトル編集" class="img-thumbnail">
            <ul>
                <li>第1話「ほげほげ」</li>
                <li>第2話「ふがふが」</li>
                <li>第2話「もがもが」<span class="text-muted">←話数が被っている</span></li>
                <li>第3話「ぴよぴよ」</li>
            </ul>
            <p>
                第２話を分解して「もがもが」を追加したとき、上記のようになっています。
                ここでタイトル変更ウインドウを開くと、下記のように正規化したタイトル候補が表示されます。
            </p>
            <ul>
                <li>第1話「ほげほげ」</li>
                <li>第2話「ふがふが」</li>
                <li><b>第3話</b>「もがもが」</li>
                <li><b>第4話</b>「ぴよぴよ」</li>
            </ul>
            <p>
                これを保存することで、正規化されたタイトルを一括修正することができます。<br>
                話数のナンバリング以外にも数字が含まれている場合、それらも同時に正規化されてしまうことで、
                作者様の想定外のタイトルになってしまうことがあります。
                それらの誤謬は適宜ウインドウ上で修正してください。
            </p>

            <h4>本文を削除する</h4>
            <p>
                本文を削除すると、即座に抹消され、もとに戻すことはできません。
            </p>
            <p>
                その本文に紐付いた拍手数はすべてクリアされます。
                本文の版も併せて削除されます。
                また、その本文に対して送られた感想については、エピソード表示が消えるだけで、感想自体は消えません。
            </p>
        </div>
    </div>
</template>

<style src="../help.scss" scoped lang="scss"></style>

<script>
export default {
    created() {
        this.useHead({
            title: "本文を管理する | 貸し本棚ヘルプ",
            link: [
                {
                    rel: "canonical",
                    href: location.origin + location.pathname,
                    id: "canonical"
                }
            ]
        });
    }
}
</script>